import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { X, House, ArrowSquareOut, CaretRight } from '@phosphor-icons/react';
import { DRAWER_GROUPS } from '../../lib/navigation';
import { hasViewAccess } from '../../lib/accessControl';

/**
 * Side drawer — secondary admin/utility links + Home shortcut.
 * Triggered from TopBar hamburger. Auto-closes on navigation (mobile-first).
 */
export default function SideDrawer({ open, onClose }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Lock background scroll on mobile when drawer is open
  useEffect(() => {
    if (open) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const go = (path) => {
    onClose();
    navigate(path);
  };

  const isOwner = user?.role === 'owner' || user?.role === 'super_admin';
  const isSuper = user?.role === 'super_admin';

  return (
    <>
      {/* Backdrop */}
      <div onClick={onClose}
        className={`fixed inset-0 z-50 bg-black/50 backdrop-blur-sm transition-opacity duration-200
          ${open ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} />

      {/* Drawer panel */}
      <aside
        data-testid="side-drawer"
        className={`fixed top-0 left-0 h-full w-[86%] max-w-sm z-50 bg-white dark:bg-stone-900 shadow-2xl
          transform transition-transform duration-300 ease-out flex flex-col
          ${open ? 'translate-x-0' : '-translate-x-full'}`}>

        {/* Header */}
        <div className="px-5 py-4 border-b border-stone-100 dark:border-stone-800 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo-round.png" alt="Cut2Pro"
              className="h-11 w-11 rounded-full shadow-sm ring-1 ring-stone-200 dark:ring-stone-700 object-cover bg-white p-0.5" />
            <div>
              <p className="text-[10px] tracking-[0.18em] uppercase text-stone-400">Welcome</p>
              <p className="text-sm font-bold text-stone-800 dark:text-stone-100 truncate">{user?.name || 'User'}</p>
            </div>
          </div>
          <button onClick={onClose} data-testid="drawer-close"
            className="h-9 w-9 rounded-lg hover:bg-stone-100 dark:hover:bg-stone-800 flex items-center justify-center text-stone-500">
            <X size={18} />
          </button>
        </div>

        {/* Content */}
        <nav className="flex-1 overflow-y-auto py-3">
          {/* Home shortcut */}
          <button onClick={() => go('/dashboard')} data-testid="drawer-home"
            className="w-full flex items-center gap-3 px-5 py-3 hover:bg-stone-50 dark:hover:bg-stone-800/50">
            <div className="h-9 w-9 rounded-lg bg-amber-50 dark:bg-amber-500/15 text-amber-600 flex items-center justify-center">
              <House size={16} weight="fill" />
            </div>
            <div className="flex-1 text-left">
              <p className="text-sm font-semibold text-stone-800 dark:text-stone-100">Home Dashboard</p>
              <p className="text-[11px] text-stone-500">Today's command centre</p>
            </div>
            <CaretRight size={14} className="text-stone-300" />
          </button>

          {DRAWER_GROUPS.map((group, gi) => {
            const items = group.items.filter(it => {
              if (it.ownerOnly && !isOwner) return false;
              if (it.superAdminOnly && !isSuper) return false;
              // Strip query-string when checking permissions (e.g. /settings?tab=users → /settings)
              const checkPath = it.path.split('?')[0];
              return hasViewAccess(user, checkPath);
            });
            if (items.length === 0) return null;
            return (
              <div key={gi} className="mt-3 pt-3 border-t border-stone-100 dark:border-stone-800">
                <p className="px-5 pb-1 text-[10px] uppercase tracking-[0.18em] text-stone-400">{group.label}</p>
                {items.map(it => {
                  const Icon = it.icon;
                  const [pathname, query] = it.path.split('?');
                  const queryParams = new URLSearchParams(query || '');
                  const itemTab = queryParams.get('tab');
                  const currentSearch = new URLSearchParams(location.search);
                  const currentTab = currentSearch.get('tab');
                  // Active when same path AND same tab (or no tab specified on either side)
                  const active = location.pathname === pathname &&
                    ((itemTab && itemTab === currentTab) ||
                     (!itemTab && !currentTab) ||
                     (pathname !== '/settings' && location.pathname.startsWith(pathname)));
                  return (
                    <button key={it.path} onClick={() => go(it.path)}
                      data-testid={`drawer-${(itemTab || pathname.replace(/\//g, '-').slice(1) || 'home')}`}
                      className={`w-full flex items-center gap-3 px-5 py-3 transition
                        ${active ? 'bg-stone-50 dark:bg-stone-800/50' : 'hover:bg-stone-50 dark:hover:bg-stone-800/50'}`}>
                      <div className={`h-9 w-9 rounded-lg flex items-center justify-center
                        ${active ? 'bg-[#6B4D5F] text-white' : 'bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-400'}`}>
                        <Icon size={16} weight={active ? 'fill' : 'duotone'} />
                      </div>
                      <div className="flex-1 text-left min-w-0">
                        <p className="text-sm font-semibold text-stone-800 dark:text-stone-100 truncate">{it.label}</p>
                        <p className="text-[11px] text-stone-500 truncate">{it.desc}</p>
                      </div>
                      <CaretRight size={14} className="text-stone-300" />
                    </button>
                  );
                })}
              </div>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="border-t border-stone-100 dark:border-stone-800 px-5 py-3 flex items-center justify-between text-[10px] text-stone-400">
          <span>Cut2Pro v1.0</span>
          <a href="https://www.n-gal.com" target="_blank" rel="noreferrer"
            className="flex items-center gap-1 hover:text-stone-600">
            n-gal.com <ArrowSquareOut size={10} />
          </a>
        </div>
      </aside>
    </>
  );
}
